import { createContext, useState, ReactNode, useContext, useCallback } from 'react';
import { DEFAULT_CREATE_ASSISTANT_FORM_DATA } from 'src/helpers/defaultValues';
import { AssistantData, CreateAssistantPayload } from 'src/services/AssistantService';

interface LinkedDoctors {
  name: string;
  id: string;
}

interface AssistantContextProps {
  updateCreateAssistantFormData(data: Partial<CreateAssistantPayload>): void;
  createAssistantFormData: CreateAssistantPayload;
  assistantId: string | null;
  setAssistantId(assistantId: string | null): void;
  setAssistantData(data: AssistantData | null): void;
  assistantData: AssistantData | null;
}

export const AssistantContext = createContext<AssistantContextProps>({
  updateCreateAssistantFormData: (data: Partial<CreateAssistantPayload>) => {},
  createAssistantFormData: DEFAULT_CREATE_ASSISTANT_FORM_DATA(),
  assistantId: null,
  setAssistantId: (assistantId: string | null) => {},
  setAssistantData: (data: AssistantData | null) => {},
  assistantData: null,
});

export interface AssistantProviderInterface {
  children: ReactNode;
}

export function AssistantProvider(props: AssistantProviderInterface) {
  const [assistantId, setAssistantId] = useState<string | null>(null);
  const [createAssistantFormData, setCreateAssistantFormData] = useState<CreateAssistantPayload>(
    DEFAULT_CREATE_ASSISTANT_FORM_DATA()
  );
  const [assistantData, setAssistantData] = useState<AssistantData | null>(null);

  const updateCreateAssistantFormData = useCallback((data: Partial<CreateAssistantPayload>) => {
    setCreateAssistantFormData((prevState) => {
      if (!prevState) return prevState;
      return {
        ...prevState,
        ...data,
      };
    });
  }, []);

  return (
    <AssistantContext.Provider
      value={{
        createAssistantFormData,
        updateCreateAssistantFormData,
        assistantId,
        setAssistantId,
        assistantData,
        setAssistantData,
      }}
    >
      <>{props.children}</>
    </AssistantContext.Provider>
  );
}

export function useAssistantContext() {
  const assistantContext = useContext(AssistantContext);

  return assistantContext;
}
